import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Package, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, setToken, formatApiError } from "@/lib/admin";
import { money } from "@/lib/site";

const STATUSES = [
  ["new", "New", "border-regal-amber/50 bg-regal-amber/5 text-regal-amber"],
  ["processing", "Processing", "border-[#B45309]/40 bg-[#B45309]/5 text-[#B45309]"],
  ["dispatched", "Dispatched", "border-emerald-600/40 bg-emerald-600/5 text-emerald-700"],
];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const ConfigBlock = ({ cfg, ccy }) => (
  <div className="mt-3 border border-regal-amber/30 bg-regal-amber/5 p-4" data-testid="order-flex-config">
    {cfg.hardware_price != null && (
      <div className="flex justify-between gap-4 text-xs">
        <span className="text-neutral-500">FLEX hardware</span>
        <span className="font-mono text-ink">{money(cfg.hardware_price, ccy)}</span>
      </div>
    )}
    {(cfg.software || []).map((s, i) => (
      <div key={i} className="mt-1.5 flex justify-between gap-4 text-xs" data-testid={`order-config-sw-${i}`}>
        <span className="text-neutral-500 leading-snug">{s.name}</span>
        <span className="shrink-0 font-mono text-ink">{money(s.price, ccy)}</span>
      </div>
    ))}
    {cfg.slave_to && (
      <p className="mt-2.5 border-t border-regal-amber/20 pt-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-regal-amber">
        {cfg.slave_to === "regal" ? "Slave → Regal Tuning" : "Slave → Another Master"}
      </p>
    )}
    {cfg.master_company && (
      <p className="mt-1 text-xs text-neutral-500">
        Master: <span className="font-semibold text-ink">{cfg.master_company}</span>
        {cfg.master_details ? ` — ${cfg.master_details}` : ""}
      </p>
    )}
  </div>
);

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => {
    adminApi().get("/admin/orders")
      .then((r) => setOrders(r.data))
      .catch((e) => {
        if (e.response?.status === 401) { setToken(null); nav("/admin/login"); }
        else toast.error("Failed to load orders");
      });
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (o, status) => {
    if (o.status === status) return;
    try {
      await adminApi().patch(`/admin/orders/${o.id}/status`, { status });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status } : x)));
      toast.success(`Order ${o.reference} → ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.reference} (${o.customer?.name || "customer"}) permanently?\n\nThis cannot be undone.`)) return;
    try {
      await adminApi().delete(`/admin/orders/${o.id}`);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      toast.success(`Order ${o.reference} deleted`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <main className="min-h-screen bg-surface pt-28 pb-20" data-testid="admin-orders-page">
      <div className="container-regal max-w-5xl">
        <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-regal-amber" data-testid="admin-orders-back"><ArrowLeft size={15} /> All products</Link>
        <div className="mb-8">
          <p className="overline-label mb-1">Admin</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Orders</h1>
          <p className="mt-2 text-sm text-neutral-400">Newest first. Demo checkout — payment is simulated.</p>
        </div>

        {!orders ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 animate-pulse border border-line bg-white" />)}</div>
        ) : orders.length === 0 ? (
          <div className="grid place-items-center border border-line bg-white p-16 text-center" data-testid="orders-empty">
            <Package size={36} className="text-neutral-600" />
            <p className="mt-4 text-sm text-neutral-400">No orders yet.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((o) => (
              <div key={o.id} className="border border-line bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid={`order-card-${o.reference}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-4">
                  <div>
                    <p className="font-mono text-sm font-bold text-regal-amber" data-testid={`order-ref-${o.reference}`}>{o.reference}</p>
                    <p className="mt-1 text-xs text-neutral-500">{fmtDate(o.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-ink">{o.customer?.name}{o.customer?.company ? ` — ${o.customer.company}` : ""}</p>
                    <p className="text-xs text-neutral-500">{o.customer?.email} · {o.customer?.country}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-3.5">
                  <div className="flex gap-2" data-testid={`order-status-${o.reference}`}>
                    {STATUSES.map(([val, label, cls]) => (
                      <button
                        key={val}
                        onClick={() => changeStatus(o, val)}
                        data-testid={`order-status-${o.reference}-${val}`}
                        className={`border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${(o.status || "new") === val ? cls + " font-bold" : "border-line text-neutral-400 hover:border-linebright"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => deleteOrder(o)} data-testid={`order-delete-${o.reference}`} className="inline-flex items-center gap-1.5 border border-line px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-400 hover:border-regal-red hover:text-regal-red transition-colors">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
                <div className="mt-4 space-y-4">
                  {o.items.map((it, i) => (
                    <div key={i} data-testid={`order-item-${o.reference}-${i}`}>
                      <div className="flex justify-between gap-4">
                        <p className="text-sm font-semibold text-ink">{it.name} <span className="font-mono text-xs text-neutral-500">× {it.qty}</span></p>
                        <p className="shrink-0 font-mono text-sm text-ink">{money(it.line_total, it.currency)}</p>
                      </div>
                      {it.configuration && <ConfigBlock cfg={it.configuration} ccy={it.currency} />}
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex justify-end gap-8 border-t border-line pt-4 font-mono text-xs text-neutral-500">
                  <span>Subtotal {money(o.subtotal_ex_vat, o.items[0]?.currency)}</span>
                  {o.shipping_label && <span>Shipping {o.shipping_label}</span>}
                  <span className="text-sm font-bold text-regal-amber" data-testid={`order-total-${o.reference}`}>Total {money(o.total_inc_vat, o.items[0]?.currency)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
